import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { Button } from '@/components/ui';

interface RouteLink {
  label: string;
  /** Internal route — renders <RouterLink to>. */
  to: string;
}

interface HashLink {
  label: string;
  /** Hash anchor (e.g. "#clear-family") — clicking smooth-scrolls to the
   *  matching `id` on the current page and updates `location.hash`. */
  hash: string;
}

export type PageHeaderLink = RouteLink | HashLink;

interface PageHeaderProps {
  /** Page-level label (e.g. "Products" or "About"). */
  title: string;
  /** 4 (or any small N) sibling links rendered as transparent / blue
   *  chevron-right buttons. Mix of `to` (route) and `hash` (anchor) supported. */
  links: readonly PageHeaderLink[];
  /** When set, the link whose `to` matches this path is hidden — used by
   *  the article pages to drop the current page's button from its own
   *  hero strip. */
  currentPath?: string;
}

function isHashLink(link: PageHeaderLink): link is HashLink {
  return 'hash' in link;
}

function scrollToHash(e: MouseEvent<HTMLElement>, hash: string) {
  const id = hash.replace(/^#/, '');
  const target = document.getElementById(id);
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth' });
  window.history.pushState(null, '', hash);
}

/**
 * Shared 520px page-header band used at the top of every page that has one.
 *
 * Two layout modes, based on `links.length`:
 *
 *   • Multi-link (Products): title left, aria-hidden spacer
 *     (`min-w-[100px] max-w-[200px] flex-1`), then the `<ul>` of buttons.
 *     Spacer caps the title-to-buttons gap at 200 (slack sits on the right
 *     when wider) and holds a 100px floor before the `<ul>` `flex-wrap`s
 *     to row 2. The moment the buttons can no longer fit horizontally on
 *     row 2, the layout snaps to a fully stacked layout: title alone on
 *     row 1, buttons vertical-stacked on row 2 with 16px between buttons
 *     and 40px between the title row and the buttons row. The stack
 *     threshold is computed dynamically — a hidden no-wrap copy of the
 *     `<ul>` is rendered off-screen and its `scrollWidth` is compared
 *     against the visible row's width on every container resize.
 *
 *   • Single-link (article-style pages — Technology, Our story, Contact us,
 *     Patient care): title left, single "View our catalogue" button right
 *     via `ml-auto`. Same dynamic-stack behaviour at narrow widths.
 */
export function PageHeader({ title, links, currentPath }: PageHeaderProps) {
  const visibleLinks = currentPath
    ? links.filter((link) => isHashLink(link) || link.to !== currentPath)
    : links;
  const isSingle = visibleLinks.length === 1;
  const [stacked, setStacked] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const check = () => {
      // For the single-link layout the threshold is "title + button > row";
      // for multi-link it's "ul natural width > row". We measure both via
      // the hidden ul's scrollWidth — for single-link the measure ul holds
      // just the one button, so this naturally accounts for the title too
      // by comparing against `row.clientWidth - titleWidth`.
      const overflow = isSingle
        ? measure.scrollWidth +
            (row.firstElementChild instanceof HTMLElement ? row.firstElementChild.offsetWidth : 0) >
          row.clientWidth
        : measure.scrollWidth > row.clientWidth;
      setStacked(overflow);
    };

    const observer = new ResizeObserver(check);
    observer.observe(row);
    check();
    return () => observer.disconnect();
  }, [visibleLinks, isSingle]);

  return (
    <section
      aria-labelledby="page-header-heading"
      className="relative flex min-h-[520px] items-end justify-center bg-clearlab-divider-light/20 px-6 py-14 md:px-14 md:py-14 lg:px-20 lg:py-20 xl:px-30 xl:py-20"
    >
      {/* Hidden no-wrap copy of the ul. Its scrollWidth is the natural
          horizontal width of the visible buttons (with their 100px gaps),
          used to decide whether the visible ul can stay horizontal. */}
      <ul
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute -z-10 inline-flex flex-row flex-nowrap items-end gap-x-[100px] whitespace-nowrap"
      >
        {visibleLinks.map((link, idx) => (
          <li key={`measure-${idx}`}>
            <Button variant="transparent" size="small" tone="blue" iconRight>
              {link.label}
            </Button>
          </li>
        ))}
      </ul>

      <div
        ref={rowRef}
        className={`flex w-full max-w-content-xl flex-wrap items-end ${
          stacked ? 'gap-y-10' : 'gap-y-6'
        }`}
      >
        <h1
          id="page-header-heading"
          className={`text-[24px] font-semibold leading-6 tracking-[-0.025em] text-clearlab-steel ${
            stacked ? 'w-full' : 'w-auto flex-shrink-0'
          }`}
        >
          {title}
        </h1>

        {!stacked && !isSingle ? (
          <div aria-hidden className="min-w-[100px] max-w-[200px] flex-1 self-stretch" />
        ) : null}

        <ul
          className={
            stacked
              ? 'flex w-full flex-col items-start gap-4'
              : isSingle
                ? 'ml-auto flex w-auto flex-row items-end'
                : 'flex w-auto flex-row flex-wrap items-end gap-x-[100px] gap-y-4'
          }
        >
          {visibleLinks.map((link) =>
            isHashLink(link) ? (
              <li key={link.label}>
                <Button
                  href={link.hash}
                  onClick={(e) => scrollToHash(e, link.hash)}
                  variant="transparent"
                  size="small"
                  tone="blue"
                  iconRight
                >
                  {link.label}
                </Button>
              </li>
            ) : (
              <li key={link.label}>
                <Button to={link.to} variant="transparent" size="small" tone="blue" iconRight>
                  {link.label}
                </Button>
              </li>
            ),
          )}
        </ul>
      </div>
    </section>
  );
}
